import { Text } from '@daily/shared/components/Text';
import { DARK_THEME_CLASS, useTheme } from '@daily/shared/contexts/Theme';
import { DailyReceiveSettings } from '@daily-co/daily-js';
import {
  useDaily,
  useLocalSessionId,
  useMeetingState,
  useNetwork,
  useParticipantIds,
} from '@daily-co/daily-react';
import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCurrentSpeakerId,
  useOrderedParticipantIds,
} from '/contexts/ParticipantsProvider';
import { useTracks } from '/contexts/TracksProvider';
import { useCallConfig } from '/hooks/useCallConfig';
import { useCustomIntegrations } from '/hooks/useCustomIntegrations';
import { useScreens } from '/hooks/useScreens';
import { useVideoQuality } from '/hooks/useVideoQuality';
import { SIDEBAR_WIDTH } from '/lib/constants';
import { useSidebarView } from '/lib/state/core';
import {
  useShowLocalVideo,
  useShowNames,
  useShowParticipantsBar,
} from '/lib/state/custom';
import { usePinnedId } from '/lib/state/layout';

import { ParticipantBar } from './ParticipantBar';
import { ScreensAndPins } from './ScreensAndPins';
import { SpeakerTile } from './SpeakerTile';

export const SpeakerView: React.FC<React.PropsWithChildren<unknown>> = () => {
  const daily = useDaily();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [showNames] = useShowNames();
  const [showLocalVideo] = useShowLocalVideo();
  const [showParticipantsBar] = useShowParticipantsBar();
  const { broadcastRole } = useCallConfig();
  const currentSpeakerId = useCurrentSpeakerId();
  const orderedParticipantIds = useOrderedParticipantIds();
  const [pinnedId] = usePinnedId();
  const [sidebarView] = useSidebarView();
  const { updateCamSubscriptions } = useTracks();
  const activeRef = useRef<HTMLDivElement>(null);
  const screens = useScreens();
  const localSessionId = useLocalSessionId();

  const { runningMainIntegrations } = useCustomIntegrations();
  const meetingState = useMeetingState();
  const hasMainEmbeds = runningMainIntegrations.length > 0;
  const hasScreens = screens.length > 0;

  const isAttendee = useMemo(
    () => broadcastRole === 'attendee',
    [broadcastRole]
  );

  const ownerIds = useParticipantIds({
    filter: useCallback((participant) => {
      return participant.owner && !participant.local;
    }, []),
  });

  const hasPinnedOrScreens = useMemo(
    () => hasScreens || hasMainEmbeds || Boolean(pinnedId),
    [hasMainEmbeds, hasScreens, pinnedId]
  );

  const showSidebar = useMemo(() => {
    return orderedParticipantIds.length > 0 || hasPinnedOrScreens;
  }, [hasPinnedOrScreens, orderedParticipantIds]);

  const fixedItems = useMemo(() => {
    const items: string[] = [];
    if (!isAttendee && showLocalVideo) {
      items.push(localSessionId);
    }
    if (hasPinnedOrScreens && orderedParticipantIds.length > 0) {
      items.push(orderedParticipantIds[0]);
    }
    return items;
  }, [
    hasPinnedOrScreens,
    isAttendee,
    localSessionId,
    orderedParticipantIds,
    showLocalVideo,
  ]);

  const otherItems = useMemo(() => {
    if (showParticipantsBar && orderedParticipantIds.length > 1) {
      return orderedParticipantIds.slice(hasPinnedOrScreens ? 1 : 0);
    }
    return [];
  }, [hasPinnedOrScreens, orderedParticipantIds, showParticipantsBar]);

  /**
   * Update cam subscriptions, in case ParticipantBar is not shown.
   */
  useEffect(() => {
    // Sidebar takes care of cam subscriptions for all displayed participants.
    if (showSidebar) return;
    updateCamSubscriptions([
      currentSpeakerId,
      pinnedId,
      ...screens.map((s) => s.session_id),
    ]);
  }, [
    currentSpeakerId,
    pinnedId,
    screens,
    showSidebar,
    updateCamSubscriptions,
  ]);

  const { threshold, topology } = useNetwork();
  const { videoQuality } = useVideoQuality();
  const updateSpeakerViewReceiveSettings = useCallback(async () => {
    const currentReceiveSettings = await daily.getReceiveSettings();
    const newReceiveSettings: DailyReceiveSettings = {};
    const hasPoorNetwork = ['low', 'very-low'].includes(threshold);
    const wantsLowQuality = ['bandwidth-saver', 'low'].includes(videoQuality);

    orderedParticipantIds.forEach((id) => {
      if (id === localSessionId) return;
      if (hasPoorNetwork || wantsLowQuality) {
        newReceiveSettings[id] = { video: { layer: 0 } };
      } else if (
        // Speaker view settings based on speaker status or pinned user
        (currentSpeakerId === id && !screens.length && !pinnedId) ||
        pinnedId === id
      ) {
        newReceiveSettings[id] = { video: { layer: 2 } };
      } else {
        newReceiveSettings[id] = { video: { layer: 0 } };
      }

      // Ignore call, when desired layer hasn't changed
      if (
        newReceiveSettings[id].video.layer ===
        currentReceiveSettings?.[id]?.video?.layer
      ) {
        delete newReceiveSettings[id];
      }
    });

    if (Object.keys(newReceiveSettings).length === 0) return;

    daily.updateReceiveSettings(newReceiveSettings);
  }, [
    currentSpeakerId,
    daily,
    localSessionId,
    orderedParticipantIds,
    pinnedId,
    screens.length,
    threshold,
    videoQuality,
  ]);

  useEffect(() => {
    if (!daily || meetingState !== 'joined-meeting') return;
    if (topology !== 'sfu') return;

    updateSpeakerViewReceiveSettings();
  }, [daily, meetingState, topology, updateSpeakerViewReceiveSettings]);

  return (
    <div className={classnames('speaker', DARK_THEME_CLASS)}>
      {isAttendee && ownerIds.length === 0 ? (
        <Text variant="largestrong" color="white" style={{ margin: 'auto' }}>
          {t('broadcast.hostLeft')}
        </Text>
      ) : (
        <div ref={activeRef} className="active">
          {hasMainEmbeds || hasScreens ? (
            <ScreensAndPins />
          ) : (
            <SpeakerTile
              screenRef={activeRef}
              sessionId={currentSpeakerId}
              showNames={showNames}
            />
          )}
        </div>
      )}
      <ParticipantBar
        fixed={fixedItems}
        others={otherItems}
        visible={showSidebar}
      />
      <style jsx>{`
        .speaker {
          display: flex;
          height: 100%;
          position: relative;
          width: calc(100% - ${sidebarView !== null ? SIDEBAR_WIDTH : '0'}px);
        }
        .active {
          align-items: center;
          display: flex;
          justify-content: center;
          height: 100%;
          overflow: hidden;
          position: relative;
          width: 100%;
        }
        .active :global(.activeBorder) {
          display: none;
        }
        .active :global(.tile.isScreenshare) {
          background: ${colors.custom.mainAreaBg};
        }
      `}</style>
    </div>
  );
};
